import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Form, Image, Modal, Row } from "react-bootstrap";
import "../css/CreatePostBox.css";
import { useAppContext } from "../../Context/AppContext";
import {
  BsCaretRightFill,
  BsCheckCircleFill,
  BsCircle,
  BsGlobe,
  BsPeopleFill,
} from "react-icons/bs";
import { FaImage, FaUserSlash } from "react-icons/fa6";
import { useCreatePost } from "../../api/mutations/UseCreatePost";
import { useQueryClient } from "@tanstack/react-query";
import { Post } from "../../types/Types";
import { useTranslation } from "react-i18next";
import { useLiveTranslatedInput } from "../../hooks/useLiveTranslatedInput";

const ArticleModal = lazy(() => import("./ArticleModal"));
const ImageUploadModal = lazy(() => import("./ImageUploadModal"));
const PlainPostModal = lazy(() => import("./PlainPostModal"));

export interface ArticleData {
  title: string;
  html: string;
  imageFile: File | null;
  cover: string;
  id?: number;
}

interface CreatePostBoxProps {
  setEditPostData: (post: Post | null) => void;
  editPostData: ({ Media?: string } & Post) | null;
}

const VISIBILITY_ANYONE = "anyone";
const VISIBILITY_CONNECTIONS = "connectionsOnly";
const COMMENT_ANYONE = "anyone";
const COMMENT_CONNECTIONS = "connectionsOnly";
const COMMENT_NO_ONE = "noOne";

const ARTICLE_ICON_STYLE: React.CSSProperties = { fontSize: "20px" };
const CLICKABLE_STYLE: React.CSSProperties = { cursor: "pointer" };
const BRAND_PARTNERSHIP_SWITCH_STYLE: React.CSSProperties = {
  marginRight: "-14px",
};
const PREVIEW_MODAL_BODY_STYLE: React.CSSProperties = { maxHeight: "70vh" };
const PREVIEW_IMAGE_STYLE: React.CSSProperties = { minHeight: "200px" };

function CreatePostBox({
  setEditPostData,
  editPostData,
}: CreatePostBoxProps) {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [postContent, setPostContent] = useState("");
  const {
    bindLiveText: bindPostLive,
    retranslateCurrent: retranslatePost,
    clearDraft: clearPostDraft,
    messageTargetLang: postTargetLang,
  } = useLiveTranslatedInput();
  const postContentRef = useRef(postContent);
  postContentRef.current = postContent;

  useEffect(() => {
    retranslatePost(() => postContentRef.current, setPostContent);
  }, [postTargetLang, retranslatePost]);

  const [visibility, setVisibility] = useState(VISIBILITY_ANYONE);
  const [isBrandPartnership, setIsBrandPartnership] = useState(false);
  const [commentControl, setCommentControl] = useState(COMMENT_ANYONE);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [articleData, setArticleData] = useState<ArticleData | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const closeAllModals = useCallback(() => {
    setShowModal(false);
    setShowSettingsModal(false);
    setShowCommentModal(false);
    setShowImageModal(false);
    setOpenPreview(false);
    setArticleData(null);
    setPostContent("");
    clearPostDraft();
    setEditPostData(null);
  }, [setEditPostData, clearPostDraft]);

  const onCreateSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["posts"] });
    closeAllModals();
  }, [queryClient, closeAllModals]);

  const onCreateError = useCallback((err: unknown) => {
    console.error("❌ Failed to create post:", err);
  }, []);

  const { mutate: createPost, isPending: isCreatingPost } = useCreatePost({
    onSuccess: onCreateSuccess,
    onError: onCreateError,
  });

  // Revoke preview blob URL when articleData?.imageFile changes
  useEffect(() => {
    if (!openPreview || !articleData?.imageFile) {
      setPreviewObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(articleData.imageFile);
    setPreviewObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [openPreview, articleData?.imageFile]);

  useEffect(() => {
    if (editPostData) {
      if (editPostData?.isArticle) {
        setShowArticleModal(true);
        const html = editPostData.content || "";
        const tempElement = document.createElement("div");
        tempElement.innerHTML = html;
        setPostContent(
          tempElement.innerHTML
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<\/p>/gi, "\n")
            .replace(/<[^>]+>/g, "")
            .trim()
        );
      } else if (editPostData.Media && !editPostData.isArticle) {
        setShowImageModal(true);
      } else {
        setShowModal(true);
      }
    }
  }, [editPostData]);

  const onClosePreview = useCallback(() => {
    setOpenPreview(false);
    setArticleData(null);
    setPostContent("");
    clearPostDraft();
  }, [clearPostDraft]);

  const handleImagePost = useCallback((data: { file: File | null; caption: string }) => {
    const formData = new FormData();
    if (editPostData?.id) {
      formData.append("id", String(editPostData.id));
    }
    formData.append("userId", user?.user?.id ? String(user.user.id) : "");
    formData.append("title", "");
    formData.append(
      "content",
      `<p>${data.caption.replace(/\n/g, "<br/>")}</p>`
    );
    formData.append("createdDate", new Date().toISOString());
    if (data.file) {
      formData.append("media", data.file);
    }
    createPost(formData);
  }, [editPostData?.id, user?.user?.id, createPost]);

  const handlePost = useCallback((content: string) => {
    const formData = new FormData();
    if (editPostData?.id) {
      formData.append("id", String(editPostData.id));
    }
    formData.append("userId", user?.user?.id ? String(user.user.id) : "");
    formData.append("title", "");
    formData.append("content", `<p>${content.replace(/\n/g, "<br/>")}</p>`);
    formData.append("createdDate", new Date().toISOString());

    createPost(formData);
  }, [editPostData?.id, user?.user?.id, createPost]);

  const handleArticlePost = useCallback(() => {
    const formData = new FormData();
    if (articleData?.id) {
      formData.append("id", String(articleData.id));
    }
    formData.append("userId", user?.user?.id ? String(user.user.id) : "");
    formData.append("title", articleData?.title || "");
    if (articleData?.imageFile) {
      formData.append("media", articleData.imageFile);
    }
    formData.append("articleContent", articleData?.html || "");
    formData.append("content", postContent || "");
    formData.append("createdDate", new Date().toISOString());
    formData.append("isArticle", "1");

    createPost(formData);
  }, [articleData, postContent, user?.user?.id, createPost]);

  const handleStartPost = useCallback(() => {
    closeAllModals();
    setShowModal(true);
  }, [closeAllModals]);

  const handleShowImageModal = useCallback(() => {
    setShowImageModal(true);
  }, []);

  const handleShowArticleModal = useCallback(() => {
    setShowArticleModal(true);
  }, []);

  const handleCloseSettingsModal = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  const handleSetVisibilityAnyone = useCallback(() => {
    setVisibility(VISIBILITY_ANYONE);
  }, []);

  const handleSetVisibilityConnections = useCallback(() => {
    setVisibility(VISIBILITY_CONNECTIONS);
  }, []);

  const handleOpenCommentModal = useCallback(() => {
    closeAllModals();
    setShowCommentModal(true);
  }, [closeAllModals]);

  const handleBackToSettings = useCallback(() => {
    closeAllModals();
    setShowSettingsModal(true);
  }, [closeAllModals]);

  const handleBackToPost = useCallback(() => {
    closeAllModals();
    setShowModal(true);
  }, [closeAllModals]);

  const handleCloseCommentModal = useCallback(() => {
    setShowCommentModal(false);
  }, []);

  const handleSetCommentControlAnyone = useCallback(() => {
    setCommentControl(COMMENT_ANYONE);
  }, []);

  const handleSetCommentControlConnections = useCallback(() => {
    setCommentControl(COMMENT_CONNECTIONS);
  }, []);

  const handleSetCommentControlNoOne = useCallback(() => {
    setCommentControl(COMMENT_NO_ONE);
  }, []);

  const handleArticleModalHide = useCallback(() => {
    setShowArticleModal(false);
    setEditPostData(null);
  }, [setEditPostData]);

  return (
    <>
      <Card className="shadow-lg border-0 p-3 mb-3">
        <Row className="align-items-center">
          <Col xs="auto">
            <Image
              src="/logo192.png"
              roundedCircle
              width={48}
              height={48}
              alt="Profile"
              className="d-block"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </Col>
          <Col>
            <div
              className="start-post-box w-100"
              onClick={handleStartPost}
            >
              {t("feed.startPost")}
            </div>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col
            className="d-flex align-items-center justify-content-center cursor-pointer px-0"
            onClick={handleShowImageModal}
          >
            <span className="option-icon photo-icon me-2">
              <FaImage size={20} />
            </span>
            <span>{t("feed.photo")}</span>
          </Col>
          <Col
            onClick={handleShowArticleModal}
            className="d-flex align-items-center justify-content-center cursor-pointer px-0"
          >
            <span
              className="option-icon article-icon me-2"
              style={ARTICLE_ICON_STYLE}
            >
              📝
            </span>
            <span className="text-nowrap">{t("feed.writeArticle")}</span>
          </Col>
        </Row>
      </Card>

      {/* Main Post Modal */}
      <PlainPostModal
        show={showModal}
        onHide={closeAllModals}
        handlePost={handlePost}
        isCreatingPost={isCreatingPost}
        editPostData={editPostData}
      />

      {/* Post Settings Modal */}
      <Modal
        show={showSettingsModal}
        onHide={handleCloseSettingsModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("feed.postSettings")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {/* Visibility */}
            <div
              className={`d-flex align-items-center justify-content-between p-2 rounded ${
                visibility === VISIBILITY_ANYONE ? "bg-light" : ""
              }`}
              style={CLICKABLE_STYLE}
              onClick={handleSetVisibilityAnyone}
            >
              <div className="d-flex align-items-center">
                <BsGlobe size={24} className="me-2" />
                <div>
                  <strong>{t("feed.visibility.anyone")}</strong>
                  <div className="small text-muted">{t("feed.visibility.anyoneDesc")}</div>
                </div>
              </div>
              {visibility === VISIBILITY_ANYONE ? (
                <BsCheckCircleFill size={20} className="text-primary" />
              ) : (
                <BsCircle size={20} className="text-muted" />
              )}
            </div>

            <div
              className={`d-flex align-items-center justify-content-between p-2 rounded ${
                visibility === VISIBILITY_CONNECTIONS ? "bg-light" : ""
              }`}
              style={CLICKABLE_STYLE}
              onClick={handleSetVisibilityConnections}
            >
              <div className="d-flex align-items-center">
                <BsPeopleFill size={24} className="me-2" />
                <div>
                  <strong>{t("feed.visibility.connectionsOnly")}</strong>
                  <div className="small text-muted">{t("feed.visibility.connectionsOnlyDesc")}</div>
                </div>
              </div>
              {visibility === VISIBILITY_CONNECTIONS ? (
                <BsCheckCircleFill size={20} className="text-primary" />
              ) : (
                <BsCircle size={20} className="text-muted" />
              )}
            </div>
          </div>
          <hr />
          {/* Comment control */}
          <div
            className="d-flex align-items-center justify-content-between px-1"
            style={CLICKABLE_STYLE}
            onClick={handleOpenCommentModal}
          >
            <div>
              <strong>{t("feed.commentControl")}</strong>
              <div className="small">{t(`feed.commentControl.${commentControl}`)}</div>
            </div>
            <BsCaretRightFill />
          </div>

          <div
            className={`d-flex align-items-center justify-content-between px-1 mt-3 ${
              visibility === VISIBILITY_CONNECTIONS ? "opacity-50" : ""
            }`}
            style={
              visibility === VISIBILITY_CONNECTIONS
                ? { pointerEvents: "none" as const }
                : { pointerEvents: "auto" as const }
            }
          >
            <div>
              <strong>{t("feed.brandPartnership")}</strong>
              <div className="small">{isBrandPartnership ? t("common.on") : t("common.off")}</div>
            </div>
            <Form.Check
              type="switch"
              style={BRAND_PARTNERSHIP_SWITCH_STYLE}
              id="brand-partnership-switch"
              className="mb-4 fs-4"
              checked={isBrandPartnership}
              onChange={(e) => setIsBrandPartnership(e.target.checked)}
              disabled={visibility === VISIBILITY_CONNECTIONS}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleBackToPost}
          >
            {t("auth.back")}
          </Button>
          <Button
            variant="primary"
            onClick={handleBackToPost}
          >
            {t("common.done")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Comment Control Modal */}
      <Modal
        show={showCommentModal}
        onHide={handleCloseCommentModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("feed.commentControlTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div
              className={`d-flex align-items-center justify-content-between p-2 rounded ${
                commentControl === COMMENT_ANYONE ? "bg-light" : ""
              }`}
              style={CLICKABLE_STYLE}
              onClick={handleSetCommentControlAnyone}
            >
              <div className="d-flex align-items-center">
                <BsGlobe size={30} className="me-2" />
                <div>
                  <strong>{t("feed.visibility.anyone")}</strong>
                  <div className="small text-muted">
                    {t("feed.commentControl.anyoneDesc")}
                  </div>
                </div>
              </div>
              {commentControl === COMMENT_ANYONE ? (
                <BsCheckCircleFill size={20} className="text-primary" />
              ) : (
                <BsCircle size={20} className="text-muted" />
              )}
            </div>

            <div
              className={`d-flex align-items-center justify-content-between p-2 rounded ${
                commentControl === COMMENT_CONNECTIONS ? "bg-light" : ""
              }`}
              style={CLICKABLE_STYLE}
              onClick={handleSetCommentControlConnections}
            >
              <div className="d-flex align-items-center">
                <BsPeopleFill size={30} className="me-2" />
                <div>
                  <strong>{t("feed.visibility.connectionsOnly")}</strong>
                  <div className="small text-muted">
                    {t("feed.commentControl.connectionsOnlyDesc")}
                  </div>
                </div>
              </div>
              {commentControl === COMMENT_CONNECTIONS ? (
                <BsCheckCircleFill size={20} className="text-primary" />
              ) : (
                <BsCircle size={20} className="text-muted" />
              )}
            </div>

            <div
              className={`d-flex align-items-center justify-content-between p-2 rounded ${
                commentControl === COMMENT_NO_ONE ? "bg-light" : ""
              }`}
              style={CLICKABLE_STYLE}
              onClick={handleSetCommentControlNoOne}
            >
              <div className="d-flex align-items-center">
                <FaUserSlash size={30} className="me-2" />
                <div>
                  <strong>{t("feed.commentControl.noOne")}
                  </strong>
                  <div className="small text-muted">
                    {t("feed.commentControl.noOneDesc")}
                  </div>
                </div>
              </div>
              {commentControl === COMMENT_NO_ONE ? (
                <BsCheckCircleFill size={20} className="text-primary" />
              ) : (
                <BsCircle size={20} className="text-muted" />
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleBackToSettings}
          >
            {t("auth.back")}
          </Button>
          <Button
            variant="primary"
            onClick={handleBackToSettings}
          >
            {t("common.done")}
          </Button>
        </Modal.Footer>
      </Modal>
      {showArticleModal && (
        <Suspense fallback={null}>
          <ArticleModal
            show={showArticleModal}
            onHide={handleArticleModalHide}
            setArticleData={setArticleData}
            setOpenPreview={setOpenPreview}
            editPostData={editPostData}
          />
        </Suspense>
      )}
      {showImageModal && (
        <Suspense fallback={null}>
          <ImageUploadModal
            show={showImageModal}
            onHide={closeAllModals}
            onPost={handleImagePost}
            isCreatingPost={isCreatingPost}
            editPostData={editPostData}
          />
        </Suspense>
      )}
      {/* Preview modal */}
      <Modal show={openPreview} onHide={onClosePreview} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("feed.preview")}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={PREVIEW_MODAL_BODY_STYLE} className="p-0 overflow-auto">
          <Form.Control
            as="textarea"
            rows={2}
            placeholder={t("feed.saySomethingPost")}
            lang={postTargetLang}
            value={postContent}
            onChange={(e) =>
              bindPostLive(e.target.value, setPostContent)
            }
            className="border-0"
          />
          <div className="p-4">
            {(articleData?.imageFile || articleData?.cover) && (
              <Image
                src={previewObjectUrl ?? articleData?.cover ?? ""}
                alt="Article cover preview"
                fluid
                className="rounded mb-3"
                width={800}
                height={267}
                loading="lazy"
                decoding="async"
                style={PREVIEW_IMAGE_STYLE}
              />
            )}
            <h1 className="mb-3">{articleData?.title || t("feed.untitled")}</h1>
            <div
              dangerouslySetInnerHTML={{
                __html: articleData?.html || `<p><i>${t("feed.noContent")}</i></p>`,
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <Button
            type="button"
            variant="primary"
            disabled={(!postContent.trim() && !articleData) || isCreatingPost}
            onClick={handleArticlePost}
          >
            {articleData?.id ? t("common.update") : t("common.post")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

CreatePostBox.displayName = "CreatePostBox";

export default memo(CreatePostBox);
